import { DownloadSimple } from '@phosphor-icons/react';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import BuildReportSection from './buildReportSection';
import DownloadFullReportDialogHeader from './header';
import LeftSection from './leftSection';
import { useGeneratePDF } from './pdf/workers/useRenderPDF';
import PreviewReportSection from './previewReportSection';

import { axiosInstanceForTenantAPI } from '../../../../assets/utils/axiosInstance';
import { downloadFileBlob } from '../../../../assets/utils/downloadFileBlob';
import {
  INITIAL_REPORT_OPTIONS,
  INITIAL_SELECTED_OPTION,
} from '../../../../constants/downloadReportOptions';
import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../hooks/global/typedReduxHooks';
import { AttachmentObject } from '../../../../interfaces/AttachmentObject';

import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '~/components/ui/dialog';
import { setTabState } from '~/slices/rfiMessageSlice';

const DownloadFullReportDialog = () => {
  const INITIAL_TAB = 0;
  const [downloadState, setDownloadState] = useState(false);
  const [selectedValue, setSelectedValue] = useState(INITIAL_SELECTED_OPTION);
  const [selectedTab, setTab] = useState(INITIAL_TAB);
  const [reportTitle, setReportTitle] = useState('');
  const [reportOptions, setReportOptions] = useState(INITIAL_REPORT_OPTIONS);
  const reportData = useAppSelector((state) => state.reportData);
  const userList = reportData.allUserTenant;
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const { blob, loading } = useGeneratePDF({
    title: reportTitle,
    options: reportOptions,
    data: reportData,
    userList,
  });

  const startDownload = async () => {
    if (!blob) return;
    const ZIP_NAME = 'ConfideReport.zip';
    const response = await axiosInstanceForTenantAPI().post(
      `/report/create/download-audit-log/${reportData.report.referenceId}`,
      { activity: ZIP_NAME },
    );
    if (response.status !== 200) {
      createToastNotification(
        intl.formatMessage({ defaultMessage: 'Error in creating audit log' }),
        'error',
      );
      setDownloadState(false);
      return;
    }
    const zip = new JSZip();
    const reportBlob = blob;
    zip.file(
      `${reportTitle !== '' ? reportTitle : 'Full Case Report'}.pdf`,
      reportBlob,
    );
    if (reportOptions.files && reportData.report.reportAttachments.length > 0) {
      const fileFolder = zip.folder('files');
      if (fileFolder) {
        const allFiles: AttachmentObject[] = [];
        reportData.report.reportAttachments.forEach((attachment) => {
          attachment.files.forEach((uploaderFile) => {
            allFiles.push(uploaderFile);
          });
        });
        const filePromises = allFiles.map((file) => {
          return downloadFileBlob(file.url);
        });
        Promise.all(filePromises)
          .then((retrievedFiles) => {
            retrievedFiles.forEach((retrievedFile, index) => {
              if (retrievedFile.data.size > 0) {
                fileFolder.file(allFiles[index].name, retrievedFile.data);
              }
            });
            zip
              .generateAsync({ type: 'blob' })
              .then((content) => {
                saveAs(content, ZIP_NAME);
              })
              .finally(() => {
                setDownloadState(false);
              });
          })
          .finally(() => {
            setDownloadState(false);
          });
      }
    } else {
      zip.generateAsync({ type: 'blob' }).then((content) => {
        setDownloadState(false);
        saveAs(content, ZIP_NAME);
      });
    }
  };

  const handleDownloadClicked = () => {
    setDownloadState(true);
    startDownload();
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          color='neutral'
          variant='soft'
          size='md'
          startDecorator={<DownloadSimple />}
          onClick={() => {
            // Note: Rfi messages are only fetched and encrypted when the user is on the rfi messages tab
            // If the user is not on the rfi messages tab, we need to fetch the rfi messages by setting the tab state
            if (reportData.rfiMessages.length === 0) {
              dispatch(setTabState('rfi-message'));
            }
          }}
        >
          <FormattedMessage defaultMessage='Download Report' />
        </Button>
      </DialogTrigger>
      <DialogContent
        size='full'
        className='absolute top-0 left-0 right-0 bottom-0'
      >
        <div className='flex flex-col h-full'>
          <DownloadFullReportDialogHeader
            selectedTab={selectedTab}
            setTab={setTab}
          />
          <div className='flex flex-row flex-grow'>
            <LeftSection
              onTitleChange={setReportTitle}
              onDownload={handleDownloadClicked}
              isDownloading={downloadState}
              isGenerating={loading}
            />
            {selectedTab === INITIAL_TAB ? (
              <BuildReportSection
                selectedValue={selectedValue}
                reportOptions={reportOptions}
                setReportOptions={setReportOptions}
                setSelectedValue={setSelectedValue}
              />
            ) : (
              <PreviewReportSection
                title={reportTitle}
                options={reportOptions}
                data={reportData}
                userList={userList}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadFullReportDialog;
